import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';

const Individual = observer(() => {
	const { assignUsersStore } = useStores();
	const { users, properties, status } = assignUsersStore;
	const tenantId = useAssignmentTenantId();

	const {
		draft: {
			customSetup: {
				selectedIndividualUsers,
				selectedIndividualPropertyIds
			}
		},
		toggleIndividualUserSelection,
		setIndividualUserSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');

	// Users are reached through properties, so scoping the property list to the
	// tenant scopes the users with it.
	useEffect(() => {
		if (
			!properties.length ||
			assignUsersStore.propertiesTenantId !== tenantId
		) {
			assignUsersStore.fetchProperties(tenantId);
		}
	}, [tenantId]);

	useEffect(() => {
		assignUsersStore.fetchUsersByProperty(selectedIndividualPropertyIds);
	}, [selectedIndividualPropertyIds]);

	const propertyOptions = properties.map((p) => ({ id: p.id, label: p.name }));

	const filtered = search.trim()
		? users.filter((u) => {
				const q = search.toLowerCase();
				return (
					u.firstName.toLowerCase().includes(q) ||
					u.lastName.toLowerCase().includes(q) ||
					u.email.toLowerCase().includes(q)
				);
		  })
		: users;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const uuid = String(id);
		const user = users.find((u) => u.id === uuid);
		if (!user) return;
		if (checked !== selectedIndividualUsers.some((u) => u.id === uuid)) {
			toggleIndividualUserSelection({
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedIndividualUsers.length === filtered.length) {
			setIndividualUserSelection([]);
		} else {
			setIndividualUserSelection(
				filtered.map((u) => ({
					id: u.id,
					firstName: u.firstName,
					lastName: u.lastName,
					email: u.email
				}))
			);
		}
	};

	const isAllSelected =
		filtered.length > 0 &&
		filtered.every((u) =>
			selectedIndividualUsers.some((su) => su.id === u.id)
		);
	const isLoading = status.fetchUsers === Status.Pending;

	return (
		<div id="lms-ca-assign-users-individual">
			<div>
				<Heading />
				<div className={styles.filters}>
					<div
						id="lms-ca-au-individual-property-filter"
						className={styles.search}
					>
						<div className={styles.selectProp}>Select Property</div>
						<MultiSelectDropdown
							options={propertyOptions}
							placeholder="Select option"
							onChange={(ids) => {
								setAssignUsersPropertyFilter(
									'selectedIndividualPropertyIds',
									ids
								);
							}}
							defaultSelectedIds={selectedIndividualPropertyIds}
						/>
					</div>
					<Input.Search
						id="lms-ca-au-individual-search"
						className={styles.search}
						placeholder="Search by name or email"
						suffix={<Icon type="search" role="img" />}
						// Keeps a suffix rendered, so the input isn't remounted mid-typing.
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</div>
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title={`Select Individuals (${filtered.length} Results)`}
				/>
				{filtered.length > 1 && (
					<div
						id="lms-ca-au-individual-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isAllSelected ? 'Deselect all' : 'Select all'}
					</div>
				)}
			</div>
			<div id="lms-ca-au-individual-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{!isLoading && selectedIndividualPropertyIds.length === 0 && (
					<div className={styles.empty}>
						Select a property to see available users.
					</div>
				)}
				{filtered.map((user) => {
					const isChecked = selectedIndividualUsers.some(
						(u) => u.id === user.id
					);
					return (
						<CheckBoxCard
							key={user.id}
							id={user.id}
							htmlId={`lms-ca-au-individual-${user.id}`}
							icon={
								<Icon
									type={
										isChecked
											? 'indiOptFilled'
											: 'individualOptIcon'
									}
								/>
							}
							title={`${user.firstName} ${user.lastName}`}
							desc={user.email}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
			</div>
		</div>
	);
});

export default Individual;
